import { Link } from 'react-router';
import { MessageCircle, Users, MessagesSquare } from 'lucide-react';
import type { Character } from '@/api';

const CATEGORY_MAP: Record<string, string> = {
  'screen-legends': 'Screen Legends',
  'toons': 'Toons',
  'irl-vibes': 'IRL Vibes',
  'memeverse': 'Memeverse',
  'original-chaos': 'Original Chaos',
};

const CATEGORY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  'screen-legends': {
    bg: 'bg-purple-500/10',
    text: 'text-purple-400',
    border: 'border-purple-500/30',
  },
  'toons': {
    bg: 'bg-pink-500/10',
    text: 'text-pink-400',
    border: 'border-pink-500/30',
  },
  'irl-vibes': {
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    border: 'border-blue-500/30',
  },
  'memeverse': {
    bg: 'bg-green-500/10',
    text: 'text-green-400',
    border: 'border-green-500/30',
  },
  'original-chaos': {
    bg: 'bg-orange-500/10',
    text: 'text-orange-400',
    border: 'border-orange-500/30',
  },
};

const getCategoryColors = (category: string) => {
  return CATEGORY_COLORS[category] || {
    bg: 'bg-[#9595a5]/10',
    text: 'text-[#9595a5]',
    border: 'border-[#9595a5]/30',
  };
};

const formatNumber = (num: number): string => {
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  return num.toString();
};

interface CharacterCardProps {
  character: Character;
}

export function CharacterCard({ character }: CharacterCardProps) {
  const categoryColors = getCategoryColors(character.category);

  return (
    <div
      data-character-card
      className="group relative bg-black border border-[#2a2a2a] rounded-xl overflow-hidden hover:border-[#3a3a3a] transition-all"
    >
      <div className="p-6">
        {/* Avatar and Category Badge */}
        <div className="flex items-start justify-between">
          <div className="relative w-16 h-16 mb-4">
            <div className="w-full h-full rounded-2xl overflow-hidden flex items-center justify-center bg-[#2a2a2a]">
              {character.imageUrl ? (
                <img
                  src={character.imageUrl}
                  alt={character.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-2xl font-semibold text-white">
                  {character.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
          </div>
          <div className="mb-3">
            <span
              className={`inline-block px-2.5 py-1 rounded-md text-xs uppercase tracking-wide font-medium border ${categoryColors.bg} ${categoryColors.text} ${categoryColors.border}`}
            >
              {CATEGORY_MAP[character.category] || character.category}
            </span>
          </div>
        </div>

        {/* Character Name */}
        <h3 className="font-['Poppins',sans-serif] text-xl font-semibold text-white mb-2">
          {character.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-[#9595a5] mb-4 line-clamp-3 min-h-[60px]">
          {character.description}
        </p>

        {/* Engagement Stats */}
        <div className="flex items-center gap-4 mb-4 pb-4 border-b border-[#2a2a2a]">
          <div className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-[#9595a5]" />
            <span className="text-xs text-[#9595a5]">
              {formatNumber(character.userCount)}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <MessagesSquare className="w-3.5 h-3.5 text-[#9595a5]" />
            <span className="text-xs text-[#9595a5]">
              {formatNumber(character.totalMessages)}
            </span>
          </div>
        </div>

        {/* Start Chat Button */}
        <Link to={`/chat/${character._id}`}>
          <button className="w-full py-3 rounded-lg bg-white text-black font-medium hover:opacity-90 transition-all flex items-center justify-center gap-2">
            <MessageCircle className="w-4 h-4" />
            Start Chat
          </button>
        </Link>
      </div>
    </div>
  );
}
